import React from 'react';
import './OptionSectionRow.scss';

type OptionSectionRowProps = {
  label: string;
  inlineControl?: React.ReactNode;
  summary?: string;
  isConfigured?: boolean;
  /** When false, hides the dot indicator entirely. Default: true when not placeholder. */
  showDot?: boolean;
  /** When true, shows a red left border on the section header (visible even when collapsed). */
  hasError?: boolean;
  /** When provided, renders a clickable "▶ Overrides" / "▼ Overrides" toggle button */
  expandLabel?: string;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  /** Content revealed when expanded */
  children?: React.ReactNode;
};

export function OptionSectionRow({
  label,
  inlineControl,
  summary,
  isConfigured = false,
  showDot,
  hasError = false,
  expandLabel,
  isExpanded,
  onExpandToggle,
  children,
}: OptionSectionRowProps) {
  const isPlaceholder = inlineControl == null;
  const hasExpand = expandLabel != null && onExpandToggle != null;

  return (
    <div
      className={`section-row-wrapper${hasError ? ' section-row-wrapper--error' : ''}`}
      role={isPlaceholder ? undefined : 'group'}
      aria-label={isPlaceholder ? undefined : label}
      {...(isPlaceholder ? { 'data-testid': 'section-row-placeholder' } : {})}
    >
      <div className="section-row">
        <span className="section-row-label">{label}</span>
        {!isPlaceholder && (
          <div className="section-row-control">{inlineControl}</div>
        )}
        <div className="section-row-right">
          {summary && (
            <span className="section-row-summary" title={summary}>
              {summary}
            </span>
          )}
          {!isPlaceholder && (showDot ?? true) && (
            <span className={`section-row-dot${isConfigured ? '' : ' section-row-dot--incomplete'}`} />
          )}
          {hasExpand && (
            <button
              type="button"
              className="section-row-expand"
              onClick={onExpandToggle}
              data-testid="section-row-expand"
            >
              <span className={`section-row-expand-chevron${isExpanded ? ' section-row-expand-chevron--open' : ''}`}>
                &#9654;
              </span>
              {expandLabel}
            </button>
          )}
        </div>
      </div>
      {hasExpand && isExpanded && children && (
        <div className="section-row-body">
          {children}
        </div>
      )}
    </div>
  );
}
